import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { api, apiError } from "../lib/api";
import { PageHeader, Card, Badge, Table, Td, Empty, Btn } from "../components/UI";
import { FSelect } from "./engagement/FormControls";
import { DateField } from "./engagement/DateField";
import { useAuth } from "../context/AuthContext";
import { usePagination, PaginationBar } from "../components/Pagination";
import { Printer, Pencil, Check, X, RotateCcw, Users, LogIn, LogOut, Search } from "lucide-react";
import { toast } from "sonner";
import { V2Preview } from "../components/V2Preview";

const TABS = [
  { key: "persons", label: "Persons Served" },
  { key: "operational", label: "Operational Census", v2: true },
  { key: "roster", label: "Client Roster", v2: true },
];

function NumberBox({ item, isAdmin, year, onSaved, wide }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState("");
  const save = async (value) => {
    try {
      await api.put("/census/overrides", { year, key: item.key, value });
      setEditing(false);
      onSaved();
      toast.success(value === null ? `${item.label} reset to auto-computed` : `${item.label} overridden`);
    } catch (err) { toast.error(apiError(err)); }
  };
  return (
    <div className={wide ? "col-span-full" : "min-w-0"}>
      <div className="mb-1.5 flex items-center gap-1.5 text-sm text-[#6D4C41]">
        <span className="truncate">{item.label}</span>
        {item.overridden && (
          <span className="inline-flex items-center gap-1 rounded-full bg-[#FFF3E0] px-2 py-0.5 text-[10px] font-semibold text-[#8D5A00]" title={`Auto-computed value: ${item.computed}`}>
            Manual
          </span>
        )}
      </div>
      {editing ? (
        <div className="flex items-center gap-1.5">
          <input data-testid={`census-edit-${item.key}`} type="number" min="0" value={val} onChange={(e) => setVal(e.target.value)} autoFocus
            className="h-12 w-full rounded-xl border border-[#8D6E63] bg-white px-4 text-[15px] font-semibold text-[#3E2723] focus:outline-none focus:ring-2 focus:ring-[#8D6E63]" />
          <button type="button" data-testid={`census-save-${item.key}`} aria-label="Save" onClick={() => val !== "" && save(Number(val))}
            className="h-9 w-9 shrink-0 rounded-full bg-[#4E342E] text-white flex items-center justify-center hover:bg-[#3E2723]"><Check size={14} /></button>
          <button type="button" aria-label="Cancel" onClick={() => setEditing(false)}
            className="h-9 w-9 shrink-0 rounded-full bg-[#F5F2EB] text-[#7B6157] flex items-center justify-center hover:bg-[#EDE7DC]"><X size={14} /></button>
        </div>
      ) : (
        <div data-testid={`census-value-${item.key}`}
          className={`group flex h-12 items-center justify-between rounded-xl border px-4 text-[15px] font-semibold ${item.overridden ? "border-[#F0C892] bg-[#FFFBF4] text-[#8A5A00]" : "border-[#E5DFD3] bg-white text-[#3E2723]"}`}>
          <span>{item.value}</span>
          {isAdmin && (
            <span className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity print:hidden">
              {item.overridden && (
                <button type="button" data-testid={`census-reset-${item.key}`} aria-label={`Reset ${item.label}`} title={`Reset to auto-computed (${item.computed})`}
                  onClick={() => save(null)} className="h-7 w-7 rounded-full bg-[#F5F2EB] text-[#7B6157] flex items-center justify-center hover:bg-[#EDE7DC]"><RotateCcw size={12} /></button>
              )}
              <button type="button" data-testid={`census-override-${item.key}`} aria-label={`Override ${item.label}`}
                onClick={() => { setVal(String(item.value)); setEditing(true); }}
                className="h-7 w-7 rounded-full bg-[#F5F2EB] text-[#7B6157] flex items-center justify-center hover:bg-[#EDE7DC]"><Pencil size={12} /></button>
            </span>
          )}
        </div>
      )}
    </div>
  );
}

const Section = ({ title, children }) => (
  <div className="border-t border-[#EFEBE0] pt-5 mt-6 first:border-0 first:pt-0 first:mt-0">
    <h2 className="font-heading text-lg font-bold text-[#3E2723] mb-4">{title}</h2>
    {children}
  </div>
);

function PersonsServed() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(thisYear);
  const [data, setData] = useState(null);
  const [dxSearch, setDxSearch] = useState("");
  const load = () => api.get("/census/persons-served", { params: { year } }).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { setData(null); load(); }, [year]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Card className="p-6" data-testid="persons-served-report">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6 print:hidden">
        <div className="text-sm text-[#7B6157] max-w-xl">
          Annual demographics report for accreditation (CARF) &amp; grant reporting. Figures are auto-computed from client records{isAdmin ? "; hover a figure to enter a manual override for paper-era data." : "."}
        </div>
        <div className="flex items-center gap-2">
          <FSelect data-testid="census-year-select" className="w-32 h-11" value={year} onChange={(e) => setYear(Number(e.target.value))}>
            {[thisYear, thisYear - 1, thisYear - 2].map((y) => <option key={y} value={y}>{y}</option>)}
          </FSelect>
          <Btn data-testid="census-print-btn" variant="outline" className="rounded-lg" onClick={() => {
            document.body.classList.add("print-face-sheet");
            window.print();
            setTimeout(() => document.body.classList.remove("print-face-sheet"), 300);
          }}><Printer size={14} /> Print Report</Btn>
        </div>
      </div>
      {!data ? <div className="py-16 text-center text-sm text-[#84675B]">Computing census…</div> : (
        <div data-print-area="true">
          <div className="hidden print:block mb-4">
            <h1 className="font-heading text-xl font-bold text-[#3E2723]">Persons Served Census - {year}</h1>
            <p className="text-xs text-[#7B6157]">Generated {dayjs().format("MM/DD/YYYY")} · Cocoa EHR</p>
          </div>
          <Section title="Persons Served">
            <div className="grid grid-cols-1 gap-4"><NumberBox item={data.total} isAdmin={isAdmin} year={year} onSaved={load} /></div>
          </Section>
          <Section title="Race / Ethnicity">
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-x-5 gap-y-4">
              {data.race.map((it) => <NumberBox key={it.key} item={it} isAdmin={isAdmin} year={year} onSaved={load} />)}
            </div>
          </Section>
          <Section title="Age">
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-x-5 gap-y-4">
              {data.age.map((it) => <NumberBox key={it.key} item={it} isAdmin={isAdmin} year={year} onSaved={load} />)}
            </div>
          </Section>
          <Section title="Other Characteristics of Persons Served">
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-x-5 gap-y-4">
              {data.characteristics.map((it) => <NumberBox key={it.key} item={it} isAdmin={isAdmin} year={year} onSaved={load} />)}
            </div>
          </Section>
          <Section title="Diagnosis">
            {data.diagnoses.length === 0 ? <Empty text="No active diagnoses recorded for this period" /> : (
              <div>
                <div className="mb-3 relative max-w-md">
                  <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#84675B]" />
                  <input data-testid="census-dx-search" value={dxSearch} onChange={(e) => setDxSearch(e.target.value)}
                    placeholder="Search diagnosis by code or description"
                    className="h-10 w-full rounded-full border border-[#E5DFD3] bg-white pl-9 pr-4 text-sm text-[#3E2723] focus:border-[#8D6E63] focus:outline-none focus:ring-1 focus:ring-[#8D6E63]" />
                </div>
                <div className="space-y-2" data-testid="census-diagnosis-list">
                  {data.diagnoses.filter((d) => {
                    const t = dxSearch.trim().toLowerCase();
                    if (!t) return true;
                    return (d.code || "").toLowerCase().includes(t) || (d.description || "").toLowerCase().includes(t);
                  }).map((d) => (
                    <div key={`${d.code}-${d.description}`} className="flex items-center gap-4 rounded-xl border border-[#E5DFD3] bg-white px-4 py-3">
                      <span className="w-16 shrink-0 font-heading font-bold text-[#3E2723] tabular-nums">{d.code}</span>
                      <span className="flex-1 min-w-0 text-sm text-[#4E342E] break-words">{d.description}</span>
                      <span className="shrink-0 rounded-full bg-[#F5F2EB] px-3 py-1 text-sm font-bold text-[#4E342E] tabular-nums">{d.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Section>
        </div>
      )}
    </Card>
  );
}

const Stat = ({ icon: Icon, label, value, tone }) => (
  <div className="flex items-center gap-4 rounded-2xl border border-[#EFEBE0] bg-white p-5 flex-1 min-w-[200px]">
    <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${tone}`}><Icon size={20} /></span>
    <div><div className="font-heading text-2xl font-bold text-[#3E2723]">{value}</div><div className="text-xs text-[#7B6157]">{label}</div></div>
  </div>
);

function Operational() {
  const navigate = useNavigate();
  const [start, setStart] = useState(dayjs().subtract(90, "day").format("YYYY-MM-DD"));
  const [end, setEnd] = useState(dayjs().format("YYYY-MM-DD"));
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/census/operational", { params: { start, end } }).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  }, [start, end]);
  if (!data) return <div className="py-16 text-center text-sm text-[#84675B]">Loading operational census…</div>;
  return (
    <div data-testid="operational-census" className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm text-[#6D4C41]">Activity period</span>
        <DateField testId="operational-start" placeholder="From" className="w-44" value={start} disabledAfter={end} onChange={setStart} />
        <DateField testId="operational-end" placeholder="To" className="w-44" value={end} disabledBefore={start} onChange={setEnd} />
      </div>
      <div className="flex flex-wrap gap-4">
        <Stat icon={Users} label="Clients currently active" value={data.active_total} tone="bg-[#EFEBE0] text-[#4E342E]" />
        <Stat icon={LogIn} label={`Admissions ${dayjs(start).format("MM/DD")} to ${dayjs(end).format("MM/DD")}`} value={data.admissions} tone="bg-[#E8F5E9] text-[#2E7D32]" />
        <Stat icon={LogOut} label={`Discharges ${dayjs(start).format("MM/DD")} to ${dayjs(end).format("MM/DD")}`} value={data.discharges} tone="bg-[#FFF3E0] text-[#8D5A00]" />
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 items-start">
        <Card data-testid="census-by-program">
          <h3 className="font-heading font-bold text-[#3E2723] p-5 pb-2">Current Census by Program</h3>
          <Table headers={["Program", "Level of Care", "Active Clients"]}>
            {data.by_program.map((p, i) => (
              <tr key={i}><Td className="font-semibold">{p.program}</Td><Td>{p.level_of_care}</Td><Td className="tabular-nums font-bold">{p.count}</Td></tr>
            ))}
          </Table>
          {data.by_program.length === 0 && <Empty text="No active admissions" />}
        </Card>
        <Card data-testid="census-activity">
          <h3 className="font-heading font-bold text-[#3E2723] p-5 pb-2">Admission / Discharge Activity</h3>
          <Table headers={["Date", "Client", "Event", "Program", "Detail"]}>
            {data.activity.map((a, i) => (
              <tr key={i} className="cursor-pointer hover:bg-[#FDFCF8]" onClick={() => navigate(`/clients/${a.client_id}`)}>
                <Td className="tabular-nums">{dayjs(a.date).format("MM/DD/YYYY")}</Td>
                <Td className="font-semibold">{a.client}</Td>
                <Td><span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${a.event === "Admission" ? "bg-[#E8F5E9] text-[#2E7D32]" : "bg-[#FFF3E0] text-[#8D5A00]"}`}>{a.event}</span></Td>
                <Td>{a.program}</Td>
                <Td className="max-w-[180px] truncate text-xs">{a.detail}</Td>
              </tr>
            ))}
          </Table>
          {data.activity.length === 0 && <Empty text="No admissions or discharges in this period" />}
        </Card>
      </div>
    </div>
  );
}

function Roster() {
  const [view, setView] = useState("active");
  const [clients, setClients] = useState([]);
  const censusPg = usePagination(clients);
  const navigate = useNavigate();
  useEffect(() => { api.get("/clients", { params: { status: view } }).then((r) => setClients(r.data)); }, [view]);
  return (
    <div>
      <div className="mb-4 flex gap-2">
        {["active", "discharged", "referred"].map((v) => (
          <button key={v} data-testid={`census-tab-${v}`} onClick={() => setView(v)}
            className={`rounded-full px-5 py-2 text-sm font-semibold capitalize transition-colors ${view === v ? "bg-[#4E342E] text-white shadow-[0_4px_12px_rgba(78,52,46,0.2)]" : "bg-[#F5F2EB] text-[#6D4C41] hover:bg-[#EDE7DC]"}`}>{v}</button>
        ))}
      </div>
      <Card>
        <Table headers={["Client", "MRN", "Admission", "Last Seen", "Diagnosis", "Status"]}>
          {censusPg.pageRows.map((c) => (
            <tr key={c.id} data-testid={`census-row-${c.id}`} onClick={() => navigate(`/clients/${c.id}`)} className="cursor-pointer hover:bg-[#FDFCF8]">
              <Td className="font-semibold">{c.first_name} {c.last_name}</Td>
              <Td>{c.mrn}</Td>
              <Td>{(c.admission_date || "").slice(0, 10)}</Td>
              <Td>{(c.last_seen || "").slice(0, 10)}</Td>
              <Td className="max-w-[240px] truncate">{c.diagnosis}</Td>
              <Td><Badge value={c.status} /></Td>
            </tr>
          ))}
        </Table>
        <PaginationBar {...censusPg} testId="census-pagination" />
        {clients.length === 0 && <Empty text={`No ${view} clients`} />}
      </Card>
    </div>
  );
}

export default function Census() {
  const [tab, setTab] = useState("persons");
  return (
    <div>
      <PageHeader title="Census" subtitle="Persons served reporting & operational client census" />
      <div className="mb-5 flex flex-wrap gap-2 print:hidden">
        {TABS.map((t) => (
          <button key={t.key} data-testid={`census-view-${t.key}`} onClick={() => setTab(t.key)}
            className={`flex items-center gap-1.5 rounded-full px-5 py-2 text-sm font-semibold transition-colors ${tab === t.key ? "bg-[#4E342E] text-white shadow-[0_4px_12px_rgba(78,52,46,0.2)]" : "bg-[#F5F2EB] text-[#6D4C41] hover:bg-[#EDE7DC]"}`}>
            {t.label}
            {t.v2 && <span className={`ml-1 rounded-full px-1.5 py-0.5 text-[9px] font-bold ${tab === t.key ? "bg-white text-[#4E342E]" : "bg-[#FFB300] text-[#3E2723]"}`}>V2</span>}
          </button>
        ))}
      </div>
      {tab === "persons" && <PersonsServed />}
      {tab === "operational" && (
        <V2Preview label="Operational Census"
          description="Daily admissions, discharges, and length-of-stay dashboard. Version 2 will add per-program breakouts, occupancy heatmaps, and CSV exports.">
          <Operational />
        </V2Preview>
      )}
      {tab === "roster" && (
        <V2Preview label="Client Roster"
          description="Complete client directory with status filters. Version 2 will add diagnosis filters, CSV export, and bulk actions.">
          <Roster />
        </V2Preview>
      )}
    </div>
  );
}
